<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Card, Table, TableHead, TableHeadCell, TableBody, TableBodyRow, TableBodyCell,
		Button, Modal, Label, Input, Select, Toggle, Checkbox, Spinner, Badge
	} from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import ServiceDiagnostics from '../../components/admin/ServiceDiagnostics.svelte';
	import RowAction from '../../components/admin/RowAction.svelte';
	import {
		listServices, getService, createService, updateService, deleteService, setServiceProbes,
		listServiceGroups, listProbes, getProbe, getServiceMetrics, setServiceMetrics, reorderServices
	} from '../../AppNetwork';
	import type { AdminService, ServiceGroup, Probe, ProbeMetric, ServiceMetric, Comparison } from '../../AppTypes';
	import { statusLabel, statusText } from '../../statusUtils';

	let services: AdminService[] = [];
	let groups: ServiceGroup[] = [];
	let probes: Probe[] = [];
	let loading = true;
	let error = '';

	let showModal = false;
	let editing: AdminService | null = null;
	let form = blankForm();
	let selectedProbeIds: number[] = [];
	let serviceMetrics: ServiceMetric[] = [];
	let probeMetricsMap: Record<number, ProbeMetric[]> = {};
	let isSaving = false;
	let expandedId: number | null = null;

	function toggleExpand(id: number) {
		expandedId = expandedId === id ? null : id;
	}

	// Drag-and-drop reordering
	let dragIndex: number | null = null;
	let overIndex: number | null = null;

	function onDragStart(i: number) {
		dragIndex = i;
		expandedId = null; // collapse diagnostics while reordering
	}
	function onDragOver(e: DragEvent, i: number) {
		e.preventDefault();
		overIndex = i;
	}
	function onDragLeave(i: number) {
		if (overIndex === i) overIndex = null;
	}
	function onDragEnd() {
		dragIndex = null;
		overIndex = null;
	}
	async function onDrop(e: DragEvent, i: number) {
		e.preventDefault();
		const from = dragIndex;
		dragIndex = null;
		overIndex = null;
		if (from === null || from === i) return;
		const next = [...services];
		const [moved] = next.splice(from, 1);
		next.splice(i, 0, moved);
		services = next;
		try {
			await reorderServices(services.map((s) => s.id));
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save new order';
			await loadAll();
		}
	}

	const comparisons: Comparison[] = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'];
	const BUILTIN_METRIC_OPTIONS = [
		{ key: '$up', label: 'Availability ($up)' },
		{ key: '$responseTime', label: 'Response time ($responseTime)' }
	];

	function probeName(id: number): string {
		return probes.find((p) => p.id === id)?.name ?? `#${id}`;
	}

	function metricKeyOptions(probeId: number): { key: string; label: string }[] {
		const provided = (probeMetricsMap[probeId] ?? []).map((m) => ({ key: m.key, label: `${m.label} (${m.key})` }));
		return [...BUILTIN_METRIC_OPTIONS, ...provided];
	}

	async function ensureProbeMetrics(probeId: number) {
		if (probeMetricsMap[probeId]) return;
		try {
			const full = await getProbe(probeId);
			probeMetricsMap = { ...probeMetricsMap, [probeId]: full.metrics ?? [] };
		} catch {
			probeMetricsMap = { ...probeMetricsMap, [probeId]: [] };
		}
	}

	function blankForm() {
		return { name: '', slug: '', description: '', groupId: null as number | null, displayOrder: 0, isPublic: true };
	}

	async function loadAll() {
		loading = true;
		error = '';
		try {
			[services, groups, probes] = await Promise.all([
				listServices(),
				listServiceGroups(),
				listProbes({ limit: 200 }).then((r) => r.items)
			]);
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load services';
		} finally {
			loading = false;
		}
	}
	onMount(loadAll);

	function groupName(id: number | null): string {
		return groups.find((g) => g.id === id)?.name ?? '—';
	}

	function openCreate() {
		editing = null; form = blankForm(); selectedProbeIds = []; serviceMetrics = []; probeMetricsMap = {}; error = ''; showModal = true;
	}
	async function openEdit(service: AdminService) {
		error = '';
		const full = await getService(service.id);
		editing = full;
		form = { name: full.name, slug: full.slug, description: full.description ?? '', groupId: full.groupId, displayOrder: full.displayOrder, isPublic: full.isPublic };
		selectedProbeIds = full.probeIds ?? [];
		probeMetricsMap = {};
		await Promise.all(selectedProbeIds.map((id) => ensureProbeMetrics(id)));
		const loaded = await getServiceMetrics(service.id);
		serviceMetrics = loaded.map((m) => ({ ...m, thresholds: (m.thresholds ?? []).map((t) => ({ ...t })) }));
		showModal = true;
	}

	async function openDuplicate(service: AdminService) {
		error = '';
		const full = await getService(service.id);
		editing = null; // create a new service from these values
		form = { name: `${full.name} (copy)`, slug: `${full.slug}-copy`, description: full.description ?? '', groupId: full.groupId, displayOrder: full.displayOrder, isPublic: full.isPublic };
		selectedProbeIds = full.probeIds ?? [];
		probeMetricsMap = {};
		await Promise.all(selectedProbeIds.map((id) => ensureProbeMetrics(id)));
		const loaded = await getServiceMetrics(service.id);
		serviceMetrics = loaded.map((m) => ({ probeId: m.probeId, metricKey: m.metricKey, enabled: m.enabled, evalMode: m.evalMode ?? 'latest', evalWindow: m.evalWindow ?? 1, thresholds: (m.thresholds ?? []).map((t) => ({ comparison: t.comparison, value: t.value, severity: t.severity, enabled: t.enabled })) }));
		showModal = true;
	}

	function addServiceMetric() {
		const probeId = selectedProbeIds[0] ?? 0;
		serviceMetrics = [...serviceMetrics, { probeId, metricKey: '$up', enabled: true, evalMode: 'latest', evalWindow: 1, thresholds: [] }];
	}
	function removeServiceMetric(index: number) {
		serviceMetrics = serviceMetrics.filter((_, i) => i !== index);
	}
	function addThreshold(index: number) {
		serviceMetrics[index].thresholds = [
			...(serviceMetrics[index].thresholds ?? []),
			{ comparison: 'gt', value: '', severity: 'degraded', enabled: true }
		];
		serviceMetrics = serviceMetrics;
	}
	function removeThreshold(index: number, ti: number) {
		serviceMetrics[index].thresholds = (serviceMetrics[index].thresholds ?? []).filter((_, i) => i !== ti);
		serviceMetrics = serviceMetrics;
	}

	async function save() {
		isSaving = true;
		error = '';
		try {
			const payload = { ...form, groupId: form.groupId === null ? null : Number(form.groupId) };
			const id = editing ? (await updateService(editing.id, payload)).id : (await createService(payload)).id;
			await setServiceProbes(id, selectedProbeIds.map(Number));
			await setServiceMetrics(id, serviceMetrics.filter((m) => m.probeId && m.metricKey));
			showModal = false;
			await loadAll();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save service';
		}
		isSaving = false;
	}

	async function remove(service: AdminService) {
		if (!confirm(`Delete service "${service.name}"?`)) return;
		await deleteService(service.id);
		await loadAll();
	}

	function toggleProbe(id: number, checked: boolean) {
		selectedProbeIds = checked ? [...selectedProbeIds, id] : selectedProbeIds.filter((p) => p !== id);
		if (checked) {
			void ensureProbeMetrics(id);
		}
	}
</script>

<PageHeader title="Services" description="Products shown on the public status page and the probes that drive their status." />

<div class="space-y-6">
	<Card class="max-w-none">
		<div class="flex flex-wrap items-center justify-end gap-2">
			<Button on:click={openCreate}>
				<i class="fa-solid fa-plus me-2"></i>New Service
			</Button>
		</div>
	</Card>

	<Card padding="none" class="max-w-none">
		{#if loading}
			<div class="p-8 text-center">
				<i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
				<p class="text-gray-500 dark:text-gray-400">Loading services...</p>
			</div>
		{:else if error}
			<div class="p-8 text-center">
				<i class="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">{error}</p>
				<Button class="mt-4" on:click={loadAll}>Retry</Button>
			</div>
		{:else if services.length === 0}
			<div class="p-8 text-center">
				<i class="fa-solid fa-server text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">No services yet</p>
			</div>
		{:else}
			<Table striped divClass="relative overflow-x-auto rounded-lg">
				<TableHead class="bg-gray-50 dark:bg-gray-700">
					<TableHeadCell class="w-8"><span class="sr-only">Reorder</span></TableHeadCell>
					<TableHeadCell class="w-8"><span class="sr-only">Expand</span></TableHeadCell>
					<TableHeadCell>Name</TableHeadCell>
					<TableHeadCell>Group</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Public</TableHeadCell>
					<TableHeadCell>Actions</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each services as service, i (service.id)}
						<tr
							class="border-b dark:border-gray-700 odd:bg-white even:bg-gray-50 odd:dark:bg-gray-800 even:dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 {overIndex === i ? 'border-t-2 border-t-primary-500' : ''} {dragIndex === i ? 'opacity-40' : ''}"
							on:dragover={(e) => onDragOver(e, i)}
							on:drop={(e) => onDrop(e, i)}
							on:dragleave={() => onDragLeave(i)}
						>
							<TableBodyCell class="pr-0">
								<span
									class="cursor-move text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
									draggable="true"
									role="button"
									tabindex="-1"
									aria-label="Drag to reorder"
									title="Drag to reorder"
									on:dragstart={() => onDragStart(i)}
									on:dragend={onDragEnd}
								>
									<i class="fa-solid fa-grip-vertical"></i>
								</span>
							</TableBodyCell>
							<TableBodyCell class="pr-0">
								<button
									type="button"
									class="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1"
									aria-label="Toggle details"
									on:click={() => toggleExpand(service.id)}
								>
									<i class="fa-solid fa-chevron-right transition-transform {expandedId === service.id ? 'rotate-90' : ''}"></i>
								</button>
							</TableBodyCell>
							<TableBodyCell class="font-medium text-gray-900 dark:text-white">{service.name}</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400">{groupName(service.groupId)}</TableBodyCell>
							<TableBodyCell><span class={statusText(service.currentStatus)}>{statusLabel(service.currentStatus)}</span></TableBodyCell>
							<TableBodyCell>
								{#if service.isPublic}<Badge color="green">Yes</Badge>{:else}<Badge color="dark">No</Badge>{/if}
							</TableBodyCell>
							<TableBodyCell>
								<div class="flex gap-1">
									<RowAction icon="fa-pen" label="Edit" on:click={() => openEdit(service)} />
									<RowAction icon="fa-copy" label="Duplicate" on:click={() => openDuplicate(service)} />
									<RowAction icon="fa-trash" label="Delete" danger on:click={() => remove(service)} />
								</div>
							</TableBodyCell>
						</tr>
						{#if expandedId === service.id}
							<TableBodyRow>
								<TableBodyCell colspan={7} class="p-0">
									<ServiceDiagnostics serviceId={service.id} />
								</TableBodyCell>
							</TableBodyRow>
						{/if}
					{/each}
				</TableBody>
			</Table>
		{/if}
	</Card>
</div>

<Modal bind:open={showModal} title={editing ? 'Edit Service' : 'New Service'} size="lg">
	<div class="space-y-4">
		<div><Label class="mb-1">Name</Label><Input bind:value={form.name} /></div>
		<div><Label class="mb-1">Slug</Label><Input bind:value={form.slug} placeholder="web-app" /></div>
		<div><Label class="mb-1">Description</Label><Input bind:value={form.description} /></div>
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Group</Label>
				<Select bind:value={form.groupId}>
					<option value={null}>— None —</option>
					{#each groups as g}<option value={g.id}>{g.name}</option>{/each}
				</Select>
			</div>
			<div><Label class="mb-1">Display order</Label><Input type="number" bind:value={form.displayOrder} /></div>
		</div>
		<Toggle bind:checked={form.isPublic}>Public</Toggle>

		<div class="border-t border-gray-200 dark:border-gray-700 pt-4">
			<Label class="mb-1">Probes</Label>
			<div class="mt-2 space-y-1 max-h-48 overflow-auto">
				{#each probes as probe}
					<Checkbox checked={selectedProbeIds.includes(probe.id)} on:change={(e) => toggleProbe(probe.id, e.currentTarget.checked)}>
						{probe.name} <span class="text-gray-400 text-xs ml-1">{probe.targetUrl}</span>
					</Checkbox>
				{/each}
				{#if probes.length === 0}<p class="text-xs text-gray-400">No probes — create probes first.</p>{/if}
			</div>
		</div>

		<div class="border-t border-gray-200 dark:border-gray-700 pt-4">
			<div class="flex items-center justify-between mb-1">
				<Label>Status metrics &amp; thresholds</Label>
				<Button size="xs" color="alternative" on:click={addServiceMetric} disabled={selectedProbeIds.length === 0}>
					<i class="fa-solid fa-plus me-1"></i>Add metric
				</Button>
			</div>
			<p class="text-xs text-gray-400 mb-3">This service's status (and incidents) come from these metrics' thresholds. Add one or more.</p>

			{#each serviceMetrics as sm, mi}
				<div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3">
					<div class="grid grid-cols-12 gap-2 items-center mb-2">
						<Select class="col-span-4" size="sm" bind:value={sm.probeId}>
							{#each selectedProbeIds as pid}<option value={pid}>{probeName(pid)}</option>{/each}
						</Select>
						<Select class="col-span-5" size="sm" bind:value={sm.metricKey}>
							{#each metricKeyOptions(sm.probeId) as opt}<option value={opt.key}>{opt.label}</option>{/each}
						</Select>
						<div class="col-span-2"><Toggle bind:checked={sm.enabled}>On</Toggle></div>
						<Button class="col-span-1" size="xs" color="red" on:click={() => removeServiceMetric(mi)}><i class="fa-solid fa-trash"></i></Button>
					</div>
					<div class="flex flex-wrap items-center gap-2 mb-2 text-xs text-gray-500 dark:text-gray-400">
						<span>Evaluate</span>
						<Select size="sm" class="!w-auto" bind:value={sm.evalMode}>
							<option value="latest">latest value</option>
							<option value="average">average of N</option>
							<option value="consistent">N consecutive breaches</option>
						</Select>
						{#if sm.evalMode && sm.evalMode !== 'latest'}
							<span>over the last</span>
							<Input size="sm" type="number" min="1" class="!w-20" bind:value={sm.evalWindow} />
							<span>{sm.evalMode === 'average' ? 'samples (mean vs thresholds)' : 'runs (all must breach)'}</span>
						{/if}
					</div>
					<div class="pl-2 border-l-2 border-gray-100 dark:border-gray-700">
						{#each sm.thresholds ?? [] as threshold, ti}
							<div class="grid grid-cols-12 gap-2 items-center mb-2">
								<span class="col-span-2 text-xs text-gray-400">when value</span>
								<Select class="col-span-2" size="sm" bind:value={threshold.comparison}>
									{#each comparisons as c}<option value={c}>{c}</option>{/each}
								</Select>
								<Input class="col-span-2" size="sm" type="text" placeholder="value" bind:value={threshold.value} />
								<span class="col-span-1 text-xs text-gray-400">→</span>
								<Select class="col-span-3" size="sm" bind:value={threshold.severity}>
									<option value="degraded">Degraded</option>
									<option value="major_outage">Outage</option>
								</Select>
								<Button class="col-span-2" size="xs" color="red" on:click={() => removeThreshold(mi, ti)}>Remove</Button>
							</div>
						{/each}
						<Button size="xs" color="alternative" on:click={() => addThreshold(mi)}><i class="fa-solid fa-plus me-1"></i>Add threshold</Button>
						{#if (sm.thresholds ?? []).length === 0}<span class="text-xs text-gray-400 ml-2">No thresholds — this metric won't affect status.</span>{/if}
					</div>
				</div>
			{/each}
			{#if serviceMetrics.length === 0}<p class="text-xs text-gray-400">No status metrics — this service stays "unknown". Add e.g. Availability ($up) eq 0 → Outage.</p>{/if}
		</div>
		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={save} disabled={isSaving}>
				{#if isSaving}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>

